import React from 'react';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import PasswordStrengthBar from 'react-password-strength-bar';
import API, { useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useSnackbar } from '@wheeebo/polaris-snackbar';
import { useErrorHandler } from '../errorHandler';
import { ValidatableField } from '../form';
import { CircleLoader } from '../loader';
const msgs = defineMessages({
    errorInvalidLogin: {
        id: 'pages.guest.SignUpPage.error.invalidLogin',
        defaultMessage: 'invalid login',
    },
    headerTitle: {
        id: 'pages.guest.SignUpPage.header.title',
        defaultMessage: 'Create Account',
    },
    buttonCancel: {
        id: 'pages.guest.SignUpPage.button.cancel',
        defaultMessage: 'Cancel',
    },
    buttonBackToLoginPage: {
        id: 'pages.guest.SignUpPage.button.backToLoginPage',
        defaultMessage: 'Back to the login page',
    },
    buttonSignUp: {
        id: 'pages.guest.SignUpPage.button.signUp',
        defaultMessage: 'Sign Up',
    },
    inputName: {
        id: 'pages.guest.SignUpPage.input.name',
        defaultMessage: 'Name',
    },
    inputEmail: {
        id: 'pages.guest.SignUpPage.input.email',
        defaultMessage: 'Email',
    },
    inputPassword: {
        id: 'pages.guest.SignUpPage.input.password',
        defaultMessage: 'Password',
    },
    textEnterEmailAndPassword: {
        id: 'pages.guest.SignUpPage.text.enterEmailAndPassword',
        defaultMessage: 'Enter your email and password. After the form submission, a confirmation email will be sent to your email.',
    },
    textSuccessMessage: {
        id: 'pages.guest.SignUpPage.text.successMessage',
        defaultMessage: 'An email was sent out. Please follow the instruction in the email to verify your email.',
    },
});
/** Reuse minimal GIS declaration defined in LoginPage */
function useGisScriptLoaded() {
    const [loaded, setLoaded] = React.useState(() => !!window.google?.accounts);
    React.useEffect(() => {
        if (loaded)
            return;
        const existing = document.querySelector('script[src*="gsi/client"]');
        if (existing) {
            setLoaded(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => setLoaded(true);
        document.head.appendChild(script);
    }, [loaded]);
    return loaded;
}
function SignUpPage({ onGoogleSignUp, googleClientId } = {}) {
    const intl = useIntl();
    const { signOut } = useAuth();
    const navigate = useNavigate();
    const { csrfToken } = useCsrfToken();
    const { showSuccess } = useSnackbar();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const { register, formState, handleSubmit: validate } = useForm();
    const googleBtnRef = React.useRef(null);
    const gisLoaded = useGisScriptLoaded();
    React.useEffect(() => {
        const googleAccounts = window.google?.accounts;
        if (!gisLoaded || !onGoogleSignUp || !googleClientId || !googleAccounts || !googleBtnRef.current)
            return;
        googleAccounts.id.initialize({
            client_id: googleClientId,
            callback: (response) => {
                if (response.credential) {
                    onGoogleSignUp(response.credential);
                }
            },
        });
        googleAccounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signup_with',
            width: '100%',
        });
    }, [gisLoaded, onGoogleSignUp, googleClientId]);
    const { errors } = formState;
    const [name, setName] = React.useState();
    const [email, setEmail] = React.useState();
    const [password, setPassword] = React.useState();
    const [strongPassword, setStrongPassword] = React.useState(false);
    const { mutate, isLoading, isSuccess } = useMutation(() => {
        if (!name || !email || !password) {
            return Promise.reject(intl.formatMessage(msgs.errorInvalidLogin));
        }
        return API.Accounts.create(csrfToken, { name, email, password });
    }, {
        onSuccess: () => {
            showSuccess('Account created');
        },
        onError: showAndEmitJsErrorEvent,
    });
    const handleSubmit = () => mutate();
    const handleGoBack = async () => {
        await signOut?.();
        navigate('/');
    };
    return (React.createElement(Container, { component: "main", sx: { display: 'flex', justifyContent: 'center' } },
        React.createElement("form", { onSubmit: validate(handleSubmit) },
            React.createElement(Paper, { sx: { width: 400, mt: 8, p: 3 } },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Typography, { variant: "h6", align: "center" }, intl.formatMessage(msgs.headerTitle)),
                    onGoogleSignUp && googleClientId && !isSuccess && (React.createElement(React.Fragment, null,
                        React.createElement(Box, { ref: googleBtnRef, sx: { width: '100%', minHeight: 44 } }),
                        React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
                            React.createElement(Box, { sx: { flex: 1, height: 1, bgcolor: 'divider' } }),
                            React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u307E\u305F\u306F"),
                            React.createElement(Box, { sx: { flex: 1, height: 1, bgcolor: 'divider' } })))),
                    React.createElement(Typography, { variant: "body1" }, intl.formatMessage(isSuccess ? msgs.textSuccessMessage : msgs.textEnterEmailAndPassword)),
                    isLoading ? React.createElement(CircleLoader, null) : null,
                    isSuccess ? (React.createElement(Button, { onClick: handleGoBack, startIcon: React.createElement(ArrowBackIcon, null) }, intl.formatMessage(msgs.buttonBackToLoginPage))) : (React.createElement(React.Fragment, null,
                        React.createElement(ValidatableField, { id: "name", autoComplete: "name", label: intl.formatMessage(msgs.inputName), fullWidth: true, disabled: isLoading, errors, register, registerOptions: {
                                required: true,
                                maxLength: 128,
                                onChange: (e) => setName(e.target.value),
                            } }),
                        React.createElement(ValidatableField, { id: "email", autoComplete: "email", type: "email", label: intl.formatMessage(msgs.inputEmail), fullWidth: true, disabled: isLoading, errors, register, registerOptions: {
                                required: true,
                                pattern: /\S+@\S+\.\S+/,
                                maxLength: 128,
                                onChange: (e) => setEmail(e.target.value),
                            } }),
                        React.createElement(Box, null,
                            React.createElement(ValidatableField, { id: "password", autoComplete: "new-password", label: intl.formatMessage(msgs.inputPassword), fullWidth: true, type: "password", disabled: isLoading, errors, register, registerOptions: {
                                    required: true,
                                    maxLength: 128,
                                    onChange: (e) => setPassword(e.target.value),
                                } }),
                            React.createElement(PasswordStrengthBar, { password: password, onChangeScore: (score) => setStrongPassword(score === 4) })),
                        React.createElement(Stack, { direction: "row", gap: 1 },
                            React.createElement(Button, { disabled: isLoading, fullWidth: true, onClick: () => navigate('/'), size: "medium" }, intl.formatMessage(msgs.buttonCancel)),
                            React.createElement(Button, { disabled: isLoading || !name || !email || !password || !strongPassword, color: "primary", fullWidth: true, type: "submit", size: "medium" }, intl.formatMessage(msgs.buttonSignUp))))))))));
}
export default React.memo(SignUpPage);
